/**
 * Context Guards
 *
 * Guards that check machine context state.
 * These don't depend on actors - they just check context.
 */

import type { ReturningPatientOutput } from "../contracts/arc-contracts";
import type { AppointmentData } from "../helper";
import type { GuardWithMetadata } from "./sms.guard";

/**
 * Requires Setup Intent Guard
 *
 * Checks if a setup intent is required (price is $0).
 * Used after calculatePaymentAmount action sets requiresSetupIntent flag.
 */
export const requiresSetupIntent: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.requiresSetupIntent === true;
  },
  metadata: {
    displayName: "requiresSetupIntent",
    description: "Check if setup intent is required (price is $0)",
    requiresContext: ["requiresSetupIntent"],
    category: "context",
  },
};

/**
 * Is Practice Id Missing Guard
 *
 * Fail-safe: a kiosk with no resolved practiceId must NOT proceed, or it would
 * silently book the check-in to a hardcoded default practice (the misrouting
 * bug). Used to route the machine to the `notConfigured` block state instead.
 */
export const isPracticeIdMissing: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      typeof context.practiceId !== "string" || context.practiceId.length === 0
    );
  },
  metadata: {
    displayName: "isPracticeIdMissing",
    description: "No practiceId resolved — kiosk is not configured",
    requiresContext: ["practiceId"],
    category: "context",
  },
};

/**
 * Is EHR Location Id Missing Guard
 *
 * Fail-safe: like practiceId, a resolved ehrLocationId is required config. It
 * feeds EHR-patient / consumer creation downstream (createUserAndConsumer and
 * the intake submission), so a kiosk with no ehrLocationId must NOT proceed, or
 * it would write patient records against a hardcoded default location. Routes
 * the machine to the same `notConfigured` block state as a missing practiceId.
 */
export const isEhrLocationIdMissing: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      typeof context.ehrLocationId !== "string" ||
      context.ehrLocationId.length === 0
    );
  },
  metadata: {
    displayName: "isEhrLocationIdMissing",
    description: "No ehrLocationId resolved — kiosk is not configured",
    requiresContext: ["ehrLocationId"],
    category: "context",
  },
};

/**
 * Has Phone Number Guard
 */
export const hasPhoneNumber: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      typeof context.phoneNumber === "string" && context.phoneNumber.length > 0
    );
  },
  metadata: {
    displayName: "hasPhoneNumber",
    description: "Phone number exists in context",
    requiresContext: ["phoneNumber"],
    category: "context",
  },
};

/**
 * Has Verification ID Guard
 */
export const hasVerificationId: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      typeof context.verificationId === "string" &&
      context.verificationId.length > 0
    );
  },
  metadata: {
    displayName: "hasVerificationId",
    description: "SMS verification ID exists in context",
    requiresContext: ["verificationId"],
    category: "context",
  },
};

/**
 * Has Patient ID Guard
 */
export const hasPatientId: GuardWithMetadata = {
  impl: ({ context }) => {
    return (
      typeof context.patientId === "string" && context.patientId.length > 0
    );
  },
  metadata: {
    displayName: "hasPatientId",
    description: "Patient ID exists in context",
    requiresContext: ["patientId"],
    category: "context",
  },
};

/**
 * Has User ID Guard
 */
export const hasUserId: GuardWithMetadata = {
  impl: ({ context }) => {
    return typeof context.userId === "string" && context.userId.length > 0;
  },
  metadata: {
    displayName: "hasUserId",
    description: "User ID exists in context",
    requiresContext: ["userId"],
    category: "context",
  },
};

/**
 * Is Authenticated Guard
 *
 * Checks if user has completed OTP verification.
 * This is different from hasUserId - a user can have a userId from lookup
 * but not be authenticated yet.
 */
export const isAuthenticated: GuardWithMetadata = {
  impl: ({ context }) => {
    // User is authenticated if they have the verified flag from OTP verification
    return context.verified === true;
  },
  metadata: {
    displayName: "isAuthenticated",
    description: "User has completed OTP verification and is authenticated",
    requiresContext: ["verified"],
    category: "context",
  },
};

/**
 * Is QR Mode Guard
 */
export const isQRMode: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.qrMode === true;
  },
  metadata: {
    displayName: "isQRMode",
    description: "Flow is in QR mode (mobile)",
    requiresContext: ["qrMode"],
    category: "context",
  },
};

/**
 * Is Kiosk Mode Guard
 */
export const isKioskMode: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.qrMode !== true;
  },
  metadata: {
    displayName: "isKioskMode",
    description: "Flow is in kiosk mode (tablet)",
    requiresContext: ["qrMode"],
    category: "context",
  },
};

/**
 * Is QR Mode AND Has Physical ID Guard (Compound)
 */
export const isQRModeAndHasPhysicalId: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context, event }: { context: any; event: any }) => {
    // Check if QR mode
    const isQR = context.qrMode === true;

    // Check if has physical ID (check event.data first, guards run before actions)
    let hasPhysical = false;
    if (event.data && typeof event.data.hasPhysicalId === "boolean") {
      hasPhysical = event.data.hasPhysicalId === true;
    } else {
      hasPhysical = context.hasPhysicalId === true;
    }

    console.log(
      "[isQRModeAndHasPhysicalId] isQR:",
      isQR,
      "hasPhysical:",
      hasPhysical,
    );

    return isQR && hasPhysical;
  },
  metadata: {
    displayName: "isQRModeAndHasPhysicalId",
    description: "Flow is in QR mode AND user has physical ID",
    requiresContext: ["qrMode", "hasPhysicalId"],
    category: "context",
  },
};

/**
 * Is QR Mode AND Has Physical ID Guard (Compound)
 */
export const isQrMode: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context, event }: { context: any; event: any }) => {
    // Check if QR mode
    const isQR = context.qrMode === true;

    return isQR;
  },
  metadata: {
    displayName: "isQRModeAndHasPhysicalId",
    description: "Flow is in QR mode AND user has physical ID",
    requiresContext: ["qrMode", "hasPhysicalId"],
    category: "context",
  },
};

/**
 * Is Kiosk Mode AND Has Physical ID Guard (Compound)
 */
export const isKioskModeAndHasPhysicalId: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context, event }: { context: any; event: any }) => {
    // Check if Kiosk mode (not QR mode)
    const isKiosk = context.qrMode !== true;

    // Check if has physical ID (check event.data first, guards run before actions)
    let hasPhysical = false;
    if (event.data && typeof event.data.hasPhysicalId === "boolean") {
      hasPhysical = event.data.hasPhysicalId === true;
    } else {
      hasPhysical = context.hasPhysicalId === true;
    }

    return isKiosk && hasPhysical;
  },
  metadata: {
    displayName: "isKioskModeAndHasPhysicalId",
    description: "Flow is in kiosk mode AND user has physical ID",
    requiresContext: ["qrMode", "hasPhysicalId"],
    category: "context",
  },
};

/**
 * Has Valid DOB Guard
 */
export const hasValidDOB: GuardWithMetadata = {
  impl: ({ context }) => {
    if (typeof context.dateOfBirth !== "string") return false;

    // Basic date validation (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(context.dateOfBirth)) return false;

    const date = new Date(context.dateOfBirth);
    return !Number.isNaN(date.getTime());
  },
  metadata: {
    displayName: "hasValidDOB",
    description: "Date of birth is valid format",
    requiresContext: ["dateOfBirth"],
    category: "validation",
  },
};

/**
 * Is Adult Guard
 */
export const isAdult: GuardWithMetadata = {
  impl: ({ context }) => {
    if (typeof context.dateOfBirth !== "string") return false;

    const dob = new Date(context.dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      return age - 1 >= 18;
    }

    return age >= 18;
  },
  metadata: {
    displayName: "isAdult",
    description: "Patient is 18 years or older",
    requiresContext: ["dateOfBirth"],
    category: "validation",
  },
};

/**
 * Is Under 18 Guard
 *
 * Checks if the patient is under 18 (minor), based on the triage arc output.
 * Used to skip the insurance arc for minors and route directly to the storefront.
 */
export const isUnder18: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.under18 === true;
  },
  metadata: {
    displayName: "isUnder18",
    description: "Patient is under 18 (minor) as determined in triage",
    requiresContext: ["under18"],
    category: "context",
  },
};

/**
 * Has HRTs Loaded Guard
 */
export const hasHrtsLoaded: GuardWithMetadata = {
  impl: ({ context }) => {
    // Check if hrts array exists and is not empty
    const hrts = context.hrts as unknown[] | undefined;
    return Array.isArray(hrts) && hrts.length > 0;
  },
  metadata: {
    displayName: "hasHrtsLoaded",
    description: "HRTs list is already loaded in context",
    requiresContext: ["hrts"],
    category: "context",
  },
};

/**
 * Has HRT IDs Guard
 */
export const hasHrtIds: GuardWithMetadata = {
  impl: ({ context }) => {
    // Check if hrtIds array exists and is not empty
    const hrtIds = context.hrtIds as number[] | undefined;
    return Array.isArray(hrtIds) && hrtIds.length > 0;
  },
  metadata: {
    displayName: "hasHrtIds",
    description: "HRT IDs list exists in context",
    requiresContext: ["hrtIds"],
    category: "context",
  },
};

/**
 * Contact Type is Phone Guard
 */
export const contactTypeIsPhone: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.contactType === "phone";
  },
  metadata: {
    displayName: "contactTypeIsPhone",
    description: "Contact type is phone (requires OTP verification)",
    requiresContext: ["contactType"],
    category: "context",
  },
};

/**
 * Contact Type is Email Guard
 */
export const contactTypeIsEmail: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.contactType === "email";
  },
  metadata: {
    displayName: "contactTypeIsEmail",
    description: "Contact type is email (skip OTP verification)",
    requiresContext: ["contactType"],
    category: "context",
  },
};

/**
 * Has Email Guard
 */
export const hasEmail: GuardWithMetadata = {
  impl: ({ context }) => {
    return typeof context.email === "string" && context.email.length > 0;
  },
  metadata: {
    displayName: "hasEmail",
    description: "Email exists in context",
    requiresContext: ["email"],
    category: "context",
  },
};

/**
 * Needs Phone Guard (authenticated with email, needs phone)
 */
export const needsPhone: GuardWithMetadata = {
  impl: ({ context }) => {
    // Check if user authenticated with email (contactType === "email")
    return context.contactType === "email";
  },
  metadata: {
    displayName: "needsPhone",
    description: "Authenticated with email, needs phone number",
    requiresContext: ["contactType"],
    category: "validation",
  },
};

/**
 * Needs Email Guard (authenticated with phone, needs email)
 */
export const needsEmail: GuardWithMetadata = {
  impl: ({ context }) => {
    // Check if user authenticated with phone (contactType === "phone")
    return context.contactType === "phone";
  },
  metadata: {
    displayName: "needsEmail",
    description: "Authenticated with phone, needs email address",
    requiresContext: ["contactType"],
    category: "validation",
  },
};

/**
 * Adding Insurance From Storefront Guard
 */
export const addingInsuranceFromStorefront: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.addingInsuranceFromStorefront === true;
  },
  metadata: {
    displayName: "addingInsuranceFromStorefront",
    description: "User is adding insurance from the storefront browse page",
    requiresContext: ["addingInsuranceFromStorefront"],
    category: "context",
  },
};

/**
 * Should Check Saved Card Guard
 *
 * Checks if we should look for a saved payment method before creating a setup intent.
 * Only true when:
 * - User is MFA'd (verified === true)
 * - It's a setup intent flow (requiresSetupIntent === true - meaning $0 or pay later)
 * - Consumer ID exists (needed to look up saved card)
 */
export const shouldCheckSavedCard: GuardWithMetadata = {
  impl: ({ context }) => {
    const isVerified = context.verified === true;
    const hasConsumerId =
      typeof context.consumerId === "string" && context.consumerId.length > 0;

    return isVerified && hasConsumerId;
  },
  metadata: {
    displayName: "shouldCheckSavedCard",
    description:
      "Check if we should look for saved payment method (MFA'd user + setup intent flow)",
    requiresContext: ["verified", "requiresSetupIntent", "consumerId"],
    category: "context",
  },
};

/**
 * Has Saved Payment Method Guard
 *
 * Checks if a saved payment method exists in context (after fetchSavedPaymentMethod actor runs).
 */
export const hasSavedPaymentMethod: GuardWithMetadata = {
  impl: ({ context }) => {
    const savedPaymentMethod = (
      context as { savedPaymentMethod?: { id: string } | null }
    ).savedPaymentMethod;
    return (
      savedPaymentMethod != null && typeof savedPaymentMethod.id === "string"
    );
  },
  metadata: {
    displayName: "hasSavedPaymentMethod",
    description: "Check if a saved payment method exists in context",
    requiresContext: ["savedPaymentMethod"],
    category: "context",
  },
};

/**
 * Is Data Already Written Guard
 *
 * Checks if patient data has already been written to the backend.
 * Used in early data write pattern to skip data writing when PAY event is triggered.
 * Returns true if appointment exists (meaning user/consumer/appointment/intake/photos are done).
 */
export const isDataAlreadyWritten: GuardWithMetadata = {
  impl: ({ context }) => {
    const appointmentId = (context as { appointment?: { id: string } })
      .appointment?.id;
    return typeof appointmentId === "string" && appointmentId.length > 0;
  },
  metadata: {
    displayName: "isDataAlreadyWritten",
    description:
      "Check if patient data has already been written (appointment exists)",
    requiresContext: ["appointment"],
    category: "context",
  },
};

/**
 * Is Data Write Before Payment Guard
 *
 * Checks if we're in the early data write flow (before payment selection).
 * Returns true if paymentRoute is null/undefined (meaning PAY event hasn't been triggered yet).
 */
export const isDataWriteBeforePayment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.paymentRoute == null;
  },
  metadata: {
    displayName: "isDataWriteBeforePayment",
    description: "Check if in early data write flow (paymentRoute not set yet)",
    requiresContext: ["paymentRoute"],
    category: "context",
  },
};

/**
 * Is Front Desk Mode Guard
 *
 * Checks if the flow is in front desk mode (FRONT_DESK signal from insurance failure).
 * Used to route through the write pipeline to completeFrontDesk instead of treatment/payment states.
 */
export const isFrontDeskMode: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.frontDeskMode === true;
  },
  metadata: {
    displayName: "isFrontDeskMode",
    description:
      "Check if in front desk mode (insurance failure → write pipeline → completeFrontDesk)",
    requiresContext: ["frontDeskMode"],
    category: "context",
  },
};

/**
 * Target Treatment State Guards
 *
 * Guards to check which treatment state to route to after early data write.
 */
export const targetIsDefaultTreatment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.targetTreatmentState === "defaultTreatment";
  },
  metadata: {
    displayName: "targetIsDefaultTreatment",
    description: "Target treatment state is defaultTreatment",
    requiresContext: ["targetTreatmentState"],
    category: "context",
  },
};

export const targetIsSpecificTreatment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.targetTreatmentState === "specificTreatment";
  },
  metadata: {
    displayName: "targetIsSpecificTreatment",
    description: "Target treatment state is specificTreatment",
    requiresContext: ["targetTreatmentState"],
    category: "context",
  },
};

export const targetIsWorkersCompTreatment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.targetTreatmentState === "workersCompTreatment";
  },
  metadata: {
    displayName: "targetIsWorkersCompTreatment",
    description: "Target treatment state is workersCompTreatment",
    requiresContext: ["targetTreatmentState"],
    category: "context",
  },
};

export const targetIsEpsTreatment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.targetTreatmentState === "epsTreatment";
  },
  metadata: {
    displayName: "targetIsEpsTreatment",
    description: "Target treatment state is epsTreatment",
    requiresContext: ["targetTreatmentState"],
    category: "context",
  },
};

export const targetIsTravelInsuranceTreatment: GuardWithMetadata = {
  impl: ({ context }) => {
    return context.targetTreatmentState === "travelInsuranceTreatment";
  },
  metadata: {
    displayName: "targetIsTravelInsuranceTreatment",
    description: "Target treatment state is travelInsuranceTreatment",
    requiresContext: ["targetTreatmentState"],
    category: "context",
  },
};

/**
 * Treatment Changed Guard
 *
 * Checks if the user selected a different treatment OR changed the pricing
 * mode after an appointment has already been created. Used to determine if
 * we need to replace the bought treatment.
 *
 * Returns true if:
 * - An appointment exists with bought treatments AND either:
 *   - The newly selected hrtId differs from the existing bought treatment's hrtId
 *   - The pricing mode changed (same treatment, different pricedTreatmentId)
 */
export const isTreatmentChanged: GuardWithMetadata = {
  impl: ({ context, event }) => {
    // Get the appointment data
    const appointment = context.appointment as
      | {
          data?: {
            boughtTreatmentWithCharges?: Array<{
              hrtId: number;
              pricedTreatmentId?: number;
            }>;
          };
        }
      | undefined;

    // If no appointment exists, treatment hasn't "changed"
    if (!appointment?.data?.boughtTreatmentWithCharges?.length) {
      return false;
    }

    const existingBoughtTreatment =
      appointment.data.boughtTreatmentWithCharges[0];
    const existingHrtId = existingBoughtTreatment?.hrtId;
    if (existingHrtId == null) {
      return false;
    }

    // Get the newly selected hrtId (could be from selectedHrt or defaultHrt)
    const selectedHrt = context.selectedHrt as { hrtId: number } | undefined;
    const defaultHrt = context.defaultHrt as { hrtId: number } | undefined;
    const newHrtId = selectedHrt?.hrtId ?? defaultHrt?.hrtId;

    if (newHrtId == null) {
      return false;
    }

    // Check 1: Treatment (HRT) itself changed
    if (existingHrtId !== newHrtId) {
      console.log(
        "[isTreatmentChanged] Treatment changed from hrtId",
        existingHrtId,
        "to",
        newHrtId,
      );
      return true;
    }

    // Check 2: Same treatment but pricing mode changed
    // Compare the pricedTreatmentId used for the existing bought treatment
    // against the currently selected pricing mode's pricedTreatmentId
    const existingPricedTreatmentId = existingBoughtTreatment.pricedTreatmentId;
    if (existingPricedTreatmentId == null) {
      return false;
    }

    const pricingMap = context.pricingMap as
      | Record<
          string,
          {
            mode: string;
            priceData: {
              CASH?: { pricedTreatmentId: number };
              INSURED?: { pricedTreatmentId: number };
            };
          }
        >
      | undefined;

    const hrtPricing = pricingMap?.[String(newHrtId)];
    if (!hrtPricing) {
      return false;
    }

    // Determine the current pricing mode:
    // Prefer event data (from PAY_NOW/PAY_AT_DESK/PAY_LATER) for robustness
    // against the SET_PRICING_MODE debounce race, then fall back to pricingMap
    const eventUseInsurance = (
      event as { data?: { useInsurance?: boolean } } | undefined
    )?.data?.useInsurance;
    const currentMode: "CASH" | "INSURED" =
      eventUseInsurance !== undefined
        ? eventUseInsurance
          ? "INSURED"
          : "CASH"
        : (hrtPricing.mode as "CASH" | "INSURED");

    const currentPricedTreatmentId =
      hrtPricing.priceData[currentMode]?.pricedTreatmentId;

    if (currentPricedTreatmentId == null) {
      return false;
    }

    // Use Number() coercion to handle type mismatch between API response
    // (may return pricedTreatmentId as string) and pricingMap (stores as number)
    const hasPricingChanged =
      Number(existingPricedTreatmentId) !== Number(currentPricedTreatmentId);

    if (hasPricingChanged) {
      console.log(
        "[isTreatmentChanged] Pricing mode changed: existing pricedTreatmentId",
        existingPricedTreatmentId,
        "vs current",
        currentPricedTreatmentId,
        `(mode: ${currentMode})`,
      );
    }

    return hasPricingChanged;
  },
  metadata: {
    displayName: "isTreatmentChanged",
    description:
      "Check if user selected a different treatment or pricing mode after appointment was created",
    requiresContext: ["appointment", "selectedHrt", "defaultHrt", "pricingMap"],
    category: "context",
  },
};

/**
 * Is Routed From Email Match Guard
 *
 * Checks if the current routing was triggered by the email match flow.
 * Used in the returningPatient state to distinguish BACK from email-match
 * OTP (→ re-invoke identity arc at emailInput) vs normal BACK (→ triaging).
 */
export const isRoutedFromEmailMatch: GuardWithMetadata = {
  impl: ({ context }) => {
    return !!(context as { _routedFromEmailMatch?: boolean })
      ._routedFromEmailMatch;
  },
  metadata: {
    displayName: "isRoutedFromEmailMatch",
    description:
      "Current flow was routed via the email match path from identity arc",
    requiresContext: ["_routedFromEmailMatch"],
    category: "context",
  },
};

/**
 * Has Existing Appointment Guard
 *
 * Checks if an appointment with bought treatments already exists.
 * Used to determine if we're in a "change treatment" flow.
 */
/**
 * Has Selected Appointment Guard
 *
 * Checks if a returning patient selected an appointment to check in to.
 * Used to route between the appointment flow (mark as arrived) and storefront.
 */
export const hasSelectedAppointment: GuardWithMetadata = {
  impl: ({ context }) => {
    const returningOutput = context._returningPatientOutput as
      | ReturningPatientOutput
      | undefined;
    return !!(
      returningOutput?.selectedAppointmentId ||
      returningOutput?.selectedEhrAppointmentId
    );
  },
  metadata: {
    displayName: "hasSelectedAppointment",
    description:
      "Check if a returning patient selected an appointment to check in to",
    requiresContext: ["_returningPatientOutput"],
    category: "context",
  },
};

export const hasExistingAppointment: GuardWithMetadata = {
  impl: ({ context }) => {
    const appointment = context.appointment as
      | { data: AppointmentData }
      | undefined;

    console.log("appointment", appointment);

    return !!(
      appointment?.data?.boughtTreatmentWithCharges?.length &&
      appointment?.data?.slot?.id
    );
  },
  metadata: {
    displayName: "hasExistingAppointment",
    description:
      "Check if an appointment with bought treatments already exists",
    requiresContext: ["appointment"],
    category: "context",
  },
};
